import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { ScoreEntry, focusNextGridCell } from "common-fe";
import {
  scoreStepFor,
  ELIMINATION_REASONS,
  ERROR_AMOUNT_SOURCES,
  errorTierForOccurrence,
  nextErrorOccurrence,
  retierErrorOccurrences,
  ordinal,
  sheetSection as sharedSheetSection,
  isSectionJudged as sharedIsSectionJudged,
} from "score-common";
import SheetStateChip, { deriveSheetState } from "./SheetStateChip";

// Card #2411 — the numeric-only score grid. Renders ONE page for a whole
// panel of numericOnly ScoreSheets (one column per booth/judge) instead of
// RideEditor's tab-per-sheet editor. This file is deliberately SEPARATE
// from RideEditor.jsx: #2410/#2412 also touch that file, and the card's own
// "watch out" note says not to refactor the tabbed editor while building
// this. RideEditor.jsx only branches to this component and passes plain
// props/callbacks down — every mutation here goes through onPatchSheet
// (writes into the shared `sheets` array RideEditor.jsx owns) and
// onSaveSheet (the same single-sheet PUT the tabbed editor's own Save
// button already makes, scoped to one sheet at a time — batching that
// across the whole panel is #2466's "save fan-out", out of scope here).
//
// AC6 — numericOnly is read straight off each sheet by the caller
// (RideEditor.jsx only renders this component when every sheet on the ride
// already has numericOnly === true); this component itself never reads or
// re-derives it from the class.
//
// Tab order across the row (#2465) needed no wiring: DOM order already
// matches visual order (row-major, booth by booth within a movement), so
// native Tab just works. Enter (#2623) is the opposite: it does nothing by
// default, so every score cell below is wired with `gridCell` +
// `onKeyDown={focusNextGridCell}` (common-fe, shared with scribe-fe's class
// grid) to walk that same sequence. Confirm-all (#2467) is IN scope here:
// see onConfirmSheet/onConfirmAll below, mirroring the #2466 Save/Save-all
// shape (a per-booth action plus a sequential fan-out over it).

// Duplicated on purpose, not imported from RideEditor.jsx (that file is
// shared with #2410/#2412 and the card's scope note says not to touch its
// internals) — this is the same tiny, pure lookup RideEditor.jsx's own
// judgePosition() does off klass.judgeAssignments.
function judgePositionFor(klass, judgeId) {
  const assignments = klass?.judgeAssignments;
  if (!assignments) return null;
  for (const [position, ids] of Object.entries(assignments)) {
    if ((ids || []).includes(judgeId)) return position;
  }
  return null;
}

function findMovementScore(sheet, movementNumber) {
  return sheet?.movementScores?.find((m) => m.movementNumber === movementNumber)?.score;
}

// Card #2468 — per-booth section filtering on a section-judged (young-horse
// two-judge) ride. RideEditor.jsx already has this exact decision
// (sectionForSheet, #2035) for its tabbed editor, which HIDES a whole
// movement row for a judge outside their section. This grid can't do that:
// a movement row is shared across every booth's column, so hiding a row for
// one booth would hide it for every booth. Design choice (card comment):
// grey out the cell instead — see isOutOfSectionFor below, used per-cell in
// the render, not per-row.
//
// Same shared score-common primitives RideEditor.jsx's sectionForSheet
// already uses (sharedSheetSection/sharedIsSectionJudged); duplicated here
// rather than imported from RideEditor.jsx for the same "don't touch the
// shared tabbed-editor internals" reason as judgePositionFor/
// nextRuleErrorFor above. This grid only ever renders ONE test for the
// whole panel (unlike RideEditor.jsx's per-sheet `tests` map), so these
// take `test` singular instead of a map.
function sheetSectionFromScores(test, sheet) {
  return sharedSheetSection(test?.movements, sheet);
}

// RideEditor.jsx's own isSectionJudgedRide computes hasSections PER SHEET
// (tests?.[s.testId]?.movements, since its tabbed editor can have sheets on
// different tests). This grid instead reads hasSections off the single
// `test` prop for the whole panel. That's fine here, not a divergent
// re-derivation: RideEditor.jsx only ever mounts this grid with one shared
// `test` for every column (see its render call, and #2411's own doc
// comment), so `test.movements` already IS what every column renders.
function isSectionJudgedGrid(klass, test, sheets) {
  const list = sheets || [];
  const hasSections = (test?.movements ?? []).some((m) => m.section);
  if (!hasSections) return false;
  const scored = new Set(list.map((s) => sheetSectionFromScores(test, s)).filter(Boolean));
  if (sharedIsSectionJudged(scored)) return true;
  return klass?.qualityJudging?.topology === "two-judge";
}

function sectionForPositionGrid(klass, position) {
  const qj = klass?.qualityJudging;
  if (qj?.topology !== "two-judge") return null;
  if (position && position === qj.qualityPosition) return "quality";
  if (position === "C") return "technical";
  return null;
}

// Which section a booth's column may score — mirrors RideEditor.jsx's
// sectionForSheet (#2035) exactly: this sheet's own scored movements decide
// first, falling back to the class's operator-entered qualityJudging field
// only when this sheet has no marks yet. Returns null when the ride isn't
// section-judged at all, or the section is genuinely unknowable — either
// case leaves every movement editable for that column, same as before #2468.
function sectionForSheetGrid(test, klass, sheets, sheet) {
  if (!isSectionJudgedGrid(klass, test, sheets)) return null;
  const derived = sheetSectionFromScores(test, sheet);
  if (derived) return derived;
  const position = judgePositionFor(klass, sheet?.judgeId) || sheet?.judgeId || null;
  return sectionForPositionGrid(klass, position);
}

function isScoredZero(value) {
  return typeof value === "number" && value === 0;
}

function formatDeductionAmount(points, percent) {
  if (typeof points === "number") return `−${points} pt${points === 1 ? "" : "s"}`;
  if (typeof percent === "number") return `−${percent}%`;
  return "";
}

// Mirrors RideEditor.jsx's own nextRuleError() — same shared score-common
// primitives (errorTierForOccurrence/nextErrorOccurrence/ordinal), so the
// Nth error of course resolves to the same deduction here as it does in the
// tabbed editor. Duplicated for the same "don't touch the shared file"
// reason as judgePositionFor above.
function nextRuleErrorFor(errors, eliminations, tiers) {
  const occurrence = nextErrorOccurrence(errors) + (eliminations ?? []).filter((e) => e.fromError).length;
  const tier = errorTierForOccurrence(tiers, occurrence) ?? {};
  const label = `${ordinal(occurrence)} error of course`;
  if (tier.eliminate) {
    return { eliminate: true, label };
  }
  const entry = { label, amountSource: ERROR_AMOUNT_SOURCES.RULE };
  if (tier.percent !== undefined) entry.percent = tier.percent;
  else entry.points = tier.points ?? 0;
  return entry;
}

function errorLabel(e) {
  const amount = formatDeductionAmount(e.points, e.percent);
  return `${e.label}: ${amount}`;
}

export default function NumericOnlyScoreGrid({
  test,
  klass,
  sheets,
  errorTiers,
  onPatchSheet,
  onSaveSheet,
  savingSheetId,
  saveErrors,
  onSaveAll,
  savingAll,
  saveAllResult,
  onConfirmSheet,
  confirmingSheetId,
  confirmErrors,
  onConfirmAll,
  confirmingAll,
  confirmAllRan,
  onCorrectSheet,
}) {
  const { t } = useTranslation("scoreadmin");
  const hasErrorConcept = (errorTiers ?? []).length > 0;
  const hasOpenSheet = sheets.some((s) => deriveSheetState(s) === "OPEN");

  const [elimMenu, setElimMenu] = useState(null); // { anchorEl, sheetId } | null
  const [errorPopover, setErrorPopover] = useState(null); // { anchorEl, sheetId } | null
  const [errorMode, setErrorMode] = useState(ERROR_AMOUNT_SOURCES.RULE);
  const [errorLabelDraft, setErrorLabelDraft] = useState("");
  const [errorAmountType, setErrorAmountType] = useState("points");
  const [errorAmountValue, setErrorAmountValue] = useState("");

  const movements = test?.movements ?? [];
  const collectives = test?.collectives ?? [];

  const sheetById = (id) => sheets.find((s) => s.id === id) ?? null;

  // Card #2468 — each booth's own section (null on a non-section-judged
  // ride, or when the section is genuinely unknowable — see
  // sectionForSheetGrid's doc comment). Computed once per render, off the
  // whole `sheets` array, not per-cell.
  const sectionForSheetId = Object.fromEntries(
    sheets.map((s) => [s.id, sectionForSheetGrid(test, klass, sheets, s)])
  );
  const isOutOfSectionFor = (sheet, movement) => {
    const section = sectionForSheetId[sheet.id];
    return Boolean(section) && Boolean(movement.section) && movement.section !== section;
  };

  const setMovementScoreFor = (sheetId, movementNumber, value) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const existing = sheet.movementScores ?? [];
    const idx = existing.findIndex((m) => m.movementNumber === movementNumber);
    if (value === "") {
      onPatchSheet(sheetId, { movementScores: existing.filter((m) => m.movementNumber !== movementNumber) });
      return;
    }
    const patch = { movementNumber, score: Number(value) };
    const next =
      idx >= 0 ? existing.map((m, i) => (i === idx ? { ...m, ...patch } : m)) : [...existing, patch];
    onPatchSheet(sheetId, { movementScores: next });
  };

  const setCollectiveScoreFor = (sheetId, key, value) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const prevEntry = sheet.collectives?.[key] ?? {};
    onPatchSheet(sheetId, {
      collectives: { ...(sheet.collectives ?? {}), [key]: { ...prevEntry, score: value === "" ? undefined : value } },
    });
  };

  const openElimMenu = (event, sheetId) => setElimMenu({ anchorEl: event.currentTarget, sheetId });
  const closeElimMenu = () => setElimMenu(null);

  const addEliminationFor = (sheetId, reason) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    onPatchSheet(sheetId, {
      eliminations: [...(sheet.eliminations ?? []), { id: crypto.randomUUID(), reason }],
    });
    closeElimMenu();
  };

  const removeEliminationFor = (sheetId, id) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const target = (sheet.eliminations ?? []).find((e) => e.id === id);
    const nextEliminationsRaw = (sheet.eliminations ?? []).filter((e) => e.id !== id);
    if (target?.fromError) {
      const { errors, eliminations } = retierErrorOccurrences(sheet.errors ?? [], nextEliminationsRaw, errorTiers);
      onPatchSheet(sheetId, { errors, eliminations });
    } else {
      onPatchSheet(sheetId, { eliminations: nextEliminationsRaw });
    }
  };

  const openErrorPopover = (event, sheetId) => {
    setErrorMode(hasErrorConcept ? ERROR_AMOUNT_SOURCES.RULE : ERROR_AMOUNT_SOURCES.MANUAL);
    setErrorLabelDraft("");
    setErrorAmountType("points");
    setErrorAmountValue("");
    setErrorPopover({ anchorEl: event.currentTarget, sheetId });
  };
  const closeErrorPopover = () => setErrorPopover(null);

  const confirmAddError = () => {
    if (!errorPopover) return;
    const { sheetId } = errorPopover;
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    if (errorMode === ERROR_AMOUNT_SOURCES.RULE) {
      const outcome = nextRuleErrorFor(sheet.errors ?? [], sheet.eliminations ?? [], errorTiers);
      if (outcome.eliminate) {
        onPatchSheet(sheetId, {
          eliminations: [
            ...(sheet.eliminations ?? []),
            { id: crypto.randomUUID(), reason: outcome.label, fromError: true },
          ],
        });
      } else {
        onPatchSheet(sheetId, {
          errors: [...(sheet.errors ?? []), { id: crypto.randomUUID(), ...outcome }],
        });
      }
    } else {
      const label = errorLabelDraft.trim();
      if (!label) return;
      const amountValue = Number(errorAmountValue);
      if (!Number.isFinite(amountValue)) return;
      const entry = {
        id: crypto.randomUUID(),
        label,
        amountSource: ERROR_AMOUNT_SOURCES.MANUAL,
        [errorAmountType]: amountValue,
      };
      onPatchSheet(sheetId, { errors: [...(sheet.errors ?? []), entry] });
    }
    closeErrorPopover();
  };

  const removeErrorFor = (sheetId, id) => {
    const sheet = sheetById(sheetId);
    if (!sheet) return;
    const nextErrorsRaw = (sheet.errors ?? []).filter((e) => e.id !== id);
    const { errors, eliminations } = retierErrorOccurrences(nextErrorsRaw, sheet.eliminations ?? [], errorTiers);
    onPatchSheet(sheetId, { errors, eliminations });
  };

  // #2466 — the whole-panel "Save" fan-out result. A booth's own per-column
  // save error (saveErrors[s.id], rendered in the save row below) is what
  // lets the operator retry just that one booth; this banner is the
  // page-level echo of the same outcome so a partial failure never reads as
  // a blanket "saved" or a blanket "save failed" (AC2/AC3) — it names every
  // booth in failedPositions by its judge position, or says every booth
  // saved when that array comes back empty.
  const failedPositions = saveAllResult?.failedPositions ?? [];
  const saveAllRanWithFailures = saveAllResult !== null && failedPositions.length > 0;
  const saveAllRanClean = saveAllResult !== null && failedPositions.length === 0;

  // #2467 — the whole-panel "Confirm All" fan-out result. Deliberately NOT
  // a stored result object like saveAllResult above: this is derived live
  // off confirmErrors on every render, so a later individual Confirm retry
  // on one previously-failed booth (which clears confirmErrors[sheetId] to
  // null) drops that booth out of this list for free the next render, with
  // no separate "clear the stale result" step to remember. That's the exact
  // bug the #2466 judge flagged in the Save-All banner — see this file's
  // saveAllResult comment above, which never got that fix.
  const confirmFailedPositions = sheets
    .filter((s) => confirmErrors?.[s.id])
    .map((s) => t("rideEditor.judgeLabel", { position: judgePositionFor(klass, s.judgeId) || s.judgeId }));
  const confirmAllRanWithFailures = confirmAllRan && confirmFailedPositions.length > 0;
  const confirmAllRanClean = confirmAllRan && confirmFailedPositions.length === 0;

  // #2467 (judge finding D3) — a clean Confirm All on a noSignatureNeeded
  // panel (the only kind this grid ever shows, #2407) stamps completedAt on
  // every booth in the SAME write as marksConfirmedAt, so hasOpenSheet flips
  // to false the instant the run finishes. Wrapping the whole banner Stack
  // in `hasOpenSheet &&` (as Save-All's borrowed placement did) would
  // unmount the success banner before it ever rendered — Save never hits
  // this because saving alone never closes a sheet. So only the BUTTON row
  // is gated on hasOpenSheet; every banner (save or confirm) renders off its
  // own result state regardless of whether any sheet is still open.
  return (
    <Box data-testid="numeric-only-grid">
      <Stack spacing={1} sx={{ mb: 2 }}>
        {hasOpenSheet && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              size="small"
              loading={savingAll}
              data-testid="numeric-only-grid-save-all"
              onClick={onSaveAll}
            >
              {t("rideEditor.numericGrid.saveAll")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              loading={confirmingAll}
              data-testid="numeric-only-grid-confirm-all"
              onClick={onConfirmAll}
            >
              {t("rideEditor.numericGrid.confirmAll")}
            </Button>
          </Stack>
        )}
        {saveAllRanWithFailures && (
          <Alert severity="error" data-testid="numeric-only-grid-save-all-error">
            {t("rideEditor.numericGrid.saveAllPartialFailure", { positions: failedPositions.join(", ") })}
          </Alert>
        )}
        {saveAllRanClean && (
          <Alert severity="success" data-testid="numeric-only-grid-save-all-success">
            {t("rideEditor.numericGrid.saveAllSuccess")}
          </Alert>
        )}
        {confirmAllRanWithFailures && (
          <Alert severity="error" data-testid="numeric-only-grid-confirm-all-error">
            {t("rideEditor.numericGrid.confirmAllPartialFailure", { positions: confirmFailedPositions.join(", ") })}
          </Alert>
        )}
        {confirmAllRanClean && (
          <Alert severity="success" data-testid="numeric-only-grid-confirm-all-success">
            {t("rideEditor.numericGrid.confirmAllSuccess")}
          </Alert>
        )}
      </Stack>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small" data-testid="numeric-only-grid-table">
          <TableHead>
            <TableRow>
              <TableCell>{t("rideEditor.numericGrid.descriptionHeader")}</TableCell>
              {sheets.map((s) => {
                const position = judgePositionFor(klass, s.judgeId) || s.judgeId || "?";
                const state = deriveSheetState(s);
                return (
                  <TableCell key={s.id} data-testid={`numeric-only-grid-header-${s.id}`}>
                    <Stack spacing={0.5}>
                      <Typography variant="subtitle2" color="text.primary">
                        {t("rideEditor.judgeLabel", { position })}
                        {s.completedAt ? " ✓" : ""}
                      </Typography>
                      <SheetStateChip state={state} />
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {movements.map((m) => (
              <TableRow key={`movement-${m.movementNumber}`} data-testid={`numeric-only-grid-movement-row-${m.movementNumber}`}>
                <TableCell>
                  <Typography variant="body2" data-testid={`numeric-only-grid-movement-desc-${m.movementNumber}`}>
                    {m.movementNumber}. {m.description}
                  </Typography>
                </TableCell>
                {sheets.map((s) => {
                  // #2468 — a movement outside this booth's own section on a
                  // section-judged ride is greyed out: no input at all (never
                  // shown or keyed, the #2035 rule this card extends to the
                  // grid), rendered before the value is even read so a stray
                  // cross-section mark (data drift, see sectionForSheetGrid's
                  // doc comment) can never leak into a cell this booth
                  // doesn't judge.
                  if (isOutOfSectionFor(s, m)) {
                    return (
                      <TableCell
                        key={s.id}
                        data-testid={`numeric-only-grid-score-cell-${s.id}-${m.movementNumber}`}
                        sx={{ bgcolor: "action.disabledBackground" }}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          data-testid={`numeric-only-grid-out-of-section-${s.id}-${m.movementNumber}`}
                        >
                          {t("rideEditor.numericGrid.outOfSection")}
                        </Typography>
                      </TableCell>
                    );
                  }
                  const editable = deriveSheetState(s) === "OPEN";
                  const value = findMovementScore(s, m.movementNumber);
                  return (
                    <TableCell key={s.id} data-testid={`numeric-only-grid-score-cell-${s.id}-${m.movementNumber}`}>
                      <ScoreEntry
                        value={value}
                        disabled={!editable}
                        coefficient={m.coefficient ?? 1}
                        step={scoreStepFor(test, m.section)}
                        strict
                        invalidLabel={t("rideEditor.scoreInvalid")}
                        onChange={(score) => setMovementScoreFor(s.id, m.movementNumber, score === undefined ? "" : score)}
                        gridCell
                        onKeyDown={focusNextGridCell}
                      />
                      {isScoredZero(value) && (
                        <Chip
                          size="small"
                          label={t("rideEditor.scoredZero")}
                          color="error"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}

            {collectives.length > 0 && (
              <TableRow>
                <TableCell colSpan={sheets.length + 1} sx={{ fontWeight: 700, bgcolor: "action.hover" }}>
                  {test.collectivesLabel || t("rideEditor.numericGrid.collectivesHeader")}
                </TableCell>
              </TableRow>
            )}
            {collectives.map((c) => {
              const key = String(c.movementNumber);
              return (
                <TableRow key={`collective-${key}`} data-testid={`numeric-only-grid-collective-row-${c.movementNumber}`}>
                  <TableCell>
                    <Typography variant="body2" data-testid={`numeric-only-grid-collective-desc-${c.movementNumber}`}>
                      {c.name}
                    </Typography>
                  </TableCell>
                  {sheets.map((s) => {
                    const editable = deriveSheetState(s) === "OPEN";
                    const value = s.collectives?.[key]?.score;
                    return (
                      <TableCell key={s.id} data-testid={`numeric-only-grid-collective-score-cell-${s.id}-${c.movementNumber}`}>
                        <ScoreEntry
                          value={value}
                          disabled={!editable}
                          coefficient={c.coefficient ?? 1}
                          step={scoreStepFor(test)}
                          strict
                          invalidLabel={t("rideEditor.scoreInvalid")}
                          onChange={(score) => setCollectiveScoreFor(s.id, key, score === undefined ? "" : score)}
                          gridCell
                          onKeyDown={focusNextGridCell}
                        />
                        {isScoredZero(value) && (
                          <Chip
                            size="small"
                            label={t("rideEditor.scoredZero")}
                            color="error"
                            variant="outlined"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}

            <TableRow data-testid="numeric-only-grid-eliminations-row">
              <TableCell>{t("rideEditor.numericGrid.eliminationsRowLabel")}</TableCell>
              {sheets.map((s) => {
                const editable = deriveSheetState(s) === "OPEN";
                return (
                  <TableCell key={s.id} data-testid={`numeric-only-grid-eliminations-${s.id}`}>
                    <Stack spacing={0.5} alignItems="flex-start">
                      {(s.eliminations ?? []).map((e) => (
                        <Chip
                          key={e.id}
                          size="small"
                          label={e.reason}
                          color="error"
                          variant="outlined"
                          onDelete={editable ? () => removeEliminationFor(s.id, e.id) : undefined}
                          data-testid={`numeric-only-grid-elimination-chip-${s.id}-${e.id}`}
                        />
                      ))}
                      {editable && (
                        <Button
                          size="small"
                          data-testid={`numeric-only-grid-add-elimination-${s.id}`}
                          onClick={(e) => openElimMenu(e, s.id)}
                        >
                          {t("rideEditor.numericGrid.addElimination")}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow data-testid="numeric-only-grid-errors-row">
              <TableCell>{t("rideEditor.numericGrid.errorsRowLabel")}</TableCell>
              {sheets.map((s) => {
                const editable = deriveSheetState(s) === "OPEN";
                return (
                  <TableCell key={s.id} data-testid={`numeric-only-grid-errors-${s.id}`}>
                    <Stack spacing={0.5} alignItems="flex-start">
                      {(s.errors ?? []).map((e) => (
                        <Chip
                          key={e.id}
                          size="small"
                          label={errorLabel(e)}
                          variant="outlined"
                          onDelete={editable ? () => removeErrorFor(s.id, e.id) : undefined}
                          data-testid={`numeric-only-grid-error-chip-${s.id}-${e.id}`}
                        />
                      ))}
                      {editable && (
                        <Button
                          size="small"
                          data-testid={`numeric-only-grid-add-error-${s.id}`}
                          onClick={(e) => openErrorPopover(e, s.id)}
                        >
                          {t("rideEditor.numericGrid.addError")}
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>

            <TableRow data-testid="numeric-only-grid-save-row">
              <TableCell />
              {sheets.map((s) => {
                const editable = deriveSheetState(s) === "OPEN";
                return (
                  <TableCell key={s.id}>
                    {editable && (
                      <>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            loading={savingSheetId === s.id}
                            data-testid={`numeric-only-grid-save-${s.id}`}
                            onClick={() => onSaveSheet(s.id)}
                          >
                            {t("rideEditor.save")}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            loading={confirmingSheetId === s.id}
                            data-testid={`numeric-only-grid-confirm-${s.id}`}
                            onClick={() => onConfirmSheet(s.id)}
                          >
                            {t("rideEditor.numericGrid.confirm")}
                          </Button>
                        </Stack>
                        {saveErrors?.[s.id] && (
                          <Alert severity="error" sx={{ mt: 0.5 }} data-testid={`numeric-only-grid-save-error-${s.id}`}>
                            {t("rideEditor.numericGrid.sheetSaveError", {
                              position: judgePositionFor(klass, s.judgeId) || s.judgeId,
                            })}
                          </Alert>
                        )}
                        {/* #2467 (D1) — a save_failed confirm error is deliberately NOT
                            rendered here: confirmGridSheet always saves before it gates
                            or confirms, so that failure already set saveErrors?.[s.id]
                            above, which renders its own "Failed to save" alert — this
                            would only duplicate it. save_failed still counts toward the
                            page-level confirmFailedPositions banner below, since confirm
                            genuinely did not happen. */}
                        {confirmErrors?.[s.id] && confirmErrors[s.id].reason !== "save_failed" && (
                          <Alert severity="error" sx={{ mt: 0.5 }} data-testid={`numeric-only-grid-confirm-error-${s.id}`}>
                            {t(
                              confirmErrors[s.id].reason === "missing_score"
                                ? "rideEditor.numericGrid.sheetConfirmMissingScoreError"
                                : "rideEditor.numericGrid.sheetConfirmError",
                              { position: judgePositionFor(klass, s.judgeId) || s.judgeId },
                            )}
                          </Alert>
                        )}
                      </>
                    )}
                    {/* #2469 — the confirm freeze is real (deriveSheetState
                        !== OPEN disables every cell above), so this is the
                        grid's ONLY affordance for a mis-keyed CONFIRMED/
                        SIGNED booth: it hands the operator to the tabbed
                        editor's existing #1067 unlock/draft/commit lifecycle
                        for just this one sheet, rather than reimplementing
                        any part of that lifecycle here. */}
                    {!editable && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="inherit"
                        data-testid={`numeric-only-grid-correct-${s.id}`}
                        onClick={() => onCorrectSheet(s.id)}
                      >
                        {t("rideEditor.numericGrid.correctSheet")}
                      </Button>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={elimMenu?.anchorEl} open={Boolean(elimMenu)} onClose={closeElimMenu}>
        {ELIMINATION_REASONS.map((reason) => (
          <MenuItem key={reason} onClick={() => addEliminationFor(elimMenu.sheetId, reason)}>
            {reason}
          </MenuItem>
        ))}
      </Menu>

      <Popover
        anchorEl={errorPopover?.anchorEl}
        open={Boolean(errorPopover)}
        onClose={closeErrorPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320 }} data-testid="numeric-only-grid-error-popover">
          <RadioGroup value={errorMode} onChange={(e) => setErrorMode(e.target.value)}>
            <FormControlLabel
              value={ERROR_AMOUNT_SOURCES.RULE}
              control={<Radio size="small" disabled={!hasErrorConcept} />}
              label={t("rideEditor.numericGrid.errorEntryModeRule")}
            />
            <FormControlLabel
              value={ERROR_AMOUNT_SOURCES.MANUAL}
              control={<Radio size="small" />}
              label={t("rideEditor.numericGrid.errorEntryModeManual")}
            />
          </RadioGroup>
          {!hasErrorConcept && errorMode === ERROR_AMOUNT_SOURCES.RULE && (
            <Typography variant="caption" color="text.secondary">
              {t("rideEditor.numericGrid.noErrorConcept")}
            </Typography>
          )}
          {errorMode === ERROR_AMOUNT_SOURCES.MANUAL && (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <TextField
                size="small"
                label={t("rideEditor.errorLabelPrompt")}
                placeholder={t("rideEditor.numericGrid.customErrorLabelPlaceholder")}
                value={errorLabelDraft}
                onChange={(e) => setErrorLabelDraft(e.target.value)}
                data-testid="numeric-only-grid-error-label-input"
              />
              <Stack direction="row" spacing={1}>
                <Select
                  size="small"
                  value={errorAmountType}
                  onChange={(e) => setErrorAmountType(e.target.value)}
                  data-testid="numeric-only-grid-error-amount-type"
                >
                  <MenuItem value="points">{t("rideEditor.numericGrid.customErrorAmountTypePoints")}</MenuItem>
                  <MenuItem value="percent">{t("rideEditor.numericGrid.customErrorAmountTypePercent")}</MenuItem>
                </Select>
                <TextField
                  size="small"
                  type="number"
                  label={t("rideEditor.errorAmountLabel")}
                  value={errorAmountValue}
                  onChange={(e) => setErrorAmountValue(e.target.value)}
                  data-testid="numeric-only-grid-error-amount-input"
                />
              </Stack>
            </Stack>
          )}
          <Button
            sx={{ mt: 2 }}
            variant="contained"
            size="small"
            onClick={confirmAddError}
            disabled={
              // #1068 fix (mirrored from RideEditor.jsx's error-dialog confirm
              // button) - rule mode has nothing to type, so the only way it
              // can't proceed is a sheet with no error-of-course schedule at
              // all, and the radio is already disabled for that. Manual mode
              // needs a label and an amount that score-common's errors[]
              // (z.number().nonnegative(), percent capped at 100) will
              // actually accept, or the PUT 400s and the whole booth's sheet
              // fails to save behind a generic error.
              errorMode === ERROR_AMOUNT_SOURCES.RULE
                ? !hasErrorConcept
                : !errorLabelDraft.trim() ||
                  errorAmountValue === "" ||
                  Number.isNaN(Number(errorAmountValue)) ||
                  Number(errorAmountValue) < 0 ||
                  (errorAmountType === "percent" && Number(errorAmountValue) > 100)
            }
            data-testid="numeric-only-grid-error-confirm"
          >
            {t("rideEditor.numericGrid.addErrorConfirm")}
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}
